import { assertIsTruthy } from '@llx/common';
import { PatientCaseContent } from '@llx/common';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { MeetingFieldsFragment, useListAnswersQuery } from '@/@generated/codegen/API';
import getAnswerItemsAccordingToUserAnswer from '@/pages/caseCluster/utils/getAnswerItemsAccordingToUserAnswer';
import { NoUserAnswer } from '@/utils/errors/NoUserAnswer';
import { captureSentry } from '@/utils/helpers/sentryHelper';

export default function usePreviousQuestionSlideUserAnswer(
  meeting: MeetingFieldsFragment | undefined | null,
  currentPatientCaseId: string | undefined | null,
  currentCase: PatientCaseContent | undefined | null,
  slideID: string,
  userID: string | undefined | null
) {
  const activated = !!userID && !!meeting?.id && !!currentPatientCaseId && !!currentCase;
  const answersForThisQuestionQuery = useListAnswersQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {
        meetingId: {
          equals: meeting?.id,
        },
        slideId: {
          equals: slideID,
        },
      },
    },
    skip: !activated,
  });
  const answersForThisQuestion = useMemo(() => {
    if (!answersForThisQuestionQuery.data) return [];
    return answersForThisQuestionQuery.data.listAnswers.edges.map((edge) => edge.node);
  }, [answersForThisQuestionQuery.data]);

  const query = useQuery({
    queryKey: ['getPreviousQuestionSlideUserAnswers'],
    refetchOnWindowFocus: false,
    enabled:
      activated && !answersForThisQuestionQuery.loading && !!answersForThisQuestionQuery.data,
    queryFn: () => {
      assertIsTruthy(currentCase);
      assertIsTruthy(userID);
      assertIsTruthy(meeting?.id);
      assertIsTruthy(currentPatientCaseId);
      assertIsTruthy(currentCase.slides.find((s) => s.id === slideID));

      try {
        const answerItems = getAnswerItemsAccordingToUserAnswer(
          userID,
          slideID,
          answersForThisQuestion,
          currentCase.slides
        );
        return answerItems;
      } catch (e) {
        //This is an error I don't want to capture
        if (e instanceof NoUserAnswer) {
          console.log('No user answer found');
          return [];
        }

        captureSentry({
          title: 'usePreviousQuestionSlideUserAnswer error',
          error: e,
          detail: {
            answersForThisQuestion,
            slideID,
            userID,
            currentCase,
          },
        });

        console.error(e);
        return [];
      }
    },
  });

  return query;
}
